import type { ReactNode } from "react"
import type { Me } from "@shared/api"
import { useAuth } from "../context/AuthContext"
import "../styles/appShell.scss"

/**
 * Shell + sidebar. Misma estructura de clases que
 * my-calendar-app/src/components/Layout/Sidebar.jsx, para que las dos apps internas se
 * naveguen igual.
 *
 * La vista activa vive en el query string (?view=…), no en un router: ver
 * hooks/useQueryState.ts para las tres razones.
 */

// "recurrences" NO está en la unión a propósito: la pantalla está apagada por ahora.
// Las tres piezas para reactivarla están listadas arriba de los lazy() de App.tsx.
export type ViewId = "requests" | "time" | "health"

interface NavItem {
  id: ViewId
  label: string
  /** Se mantiene aunque hoy las tres estén listas: una pantalla futura se muestra
   *  deshabilitada, para que el tablero declare su propio alcance en vez de que el admin
   *  descubra que falta. */
  ready: boolean
  badge?: number
}

export function AppShell({
  me,
  view,
  onNavigate,
  pendingCount,
  children,
}: {
  me: Me
  view: ViewId
  onNavigate: (v: ViewId) => void
  pendingCount?: number
  children: ReactNode
}) {
  const { signOut } = useAuth()

  // El ORDEN es el del sidebar, y el primero es además el default de App.tsx: si se
  // reordena acá hay que mover el ?? de App.tsx:32 también.
  const items: NavItem[] = [
    { id: "time", label: "Tiempo por persona", ready: true },
    { id: "health", label: "Salud operativa", ready: true },
    { id: "requests", label: "Solicitudes", ready: true, badge: pendingCount },
  ]

  return (
    <div className="side-menu-container">
      <nav className="side-menu">
        <div className="side-menu-top">
          <div className="side-menu-brand">Reuniones</div>
          <div className="side-menu-brand-sub">Tablero admin</div>

          <div className="side-menu-nav">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={item.id === view ? "active" : undefined}
                onClick={() => item.ready && onNavigate(item.id)}
                disabled={!item.ready}
                title={item.ready ? undefined : "Próximamente"}
              >
                <span>{item.label}</span>
                {item.ready && item.badge ? (
                  <span className="nav-badge">{item.badge}</span>
                ) : !item.ready ? (
                  <span className="nav-soon">pronto</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <div className="side-menu-account">
          <div className="account-email">{me.email}</div>
          <button type="button" className="btn btn-sm" onClick={() => void signOut()}>
            Cerrar sesión
          </button>
        </div>
      </nav>

      <main className="children-container">{children}</main>
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: ReactNode
  /** Slot a la derecha: el refresh manual + «actualizado hace Xs» de Salud. */
  actions?: ReactNode
}) {
  return (
    <header className="page-header" style={actions ? { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" } : undefined}>
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="page-subtitle">{subtitle}</div>}
      </div>
      {actions}
    </header>
  )
}
